import React, { useEffect, useState } from "react";
import { IncomeType, incomeTypes } from "../../../models/IncomeType";
import { Heading, Subheading } from "../../../design/heading";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { Participant } from "../../../models/Participant";
import { useIncome } from "../../../contexts/IncomeContext";

interface SidebarProps {
  participantId: Participant["_id"] | undefined;
  selectedType: IncomeType | undefined;
  onSelect: (type: IncomeType) => void;
}

const sections = {
  Employment: [
    "employment_w2",
    "bonuses_and_commissions",
    "self_employment_1099",
  ],
  "Government Assistance": [
    "disability_benefits",
    "food_stamps_snap",
    "public_assistance",
    "supplemental_social_security",
    "unemployment_benefits",
    "workers_compensation",
    "veterans_benefits",
  ],
  Retirement: [
    "pension",
    "retirement_income",
    "social_security_retirement",
    "annuity_payments",
  ],
  "Family Support": ["child_support", "spousal_support"],
  "Other Income": [
    "fellowships_stipends",
    "tax_refund",
    "rental_income",
    "interest_dividends",
    "loan_asset",
    "other_asset",
    "other_income",
    "business_interest_or_business_related",
  ],
};

const IncomeListSidebar: React.FC<SidebarProps> = ({
  participantId,
  selectedType,
  onSelect,
}) => {
  const { incomes } = useIncome();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    () => {
      try {
        const stored = localStorage.getItem("incomeSidebarOpenSections");
        return stored ? JSON.parse(stored) : { Employment: true };
      } catch (error) {
        return { Employment: true };
      }
    },
  );

  useEffect(() => {
    try {
      localStorage.setItem(
        "incomeSidebarOpenSections",
        JSON.stringify(openSections),
      );
    } catch (error) {
      console.error("Failed to save openSections:", error);
    }
  }, [openSections]);

  const toggleSection = (section: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className="space-y-4 border-r">
      {Object.entries(sections).map(([sectionName, types]) => (
        <div key={sectionName}>
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={() => toggleSection(sectionName)}
          >
            <Heading>{sectionName}</Heading>
            <span>
              {openSections[sectionName] ? (
                <ChevronDownIcon className="w-4" />
              ) : (
                <ChevronRightIcon className="w-4" />
              )}
            </span>
          </div>
          {openSections[sectionName] && (
            <div className="mt-2">
              {types.map((typeKey) => {
                const type = typeKey as IncomeType;

                // Sum income for this participant AND this type
                const incomeSum = incomes
                  .filter(
                    (income) =>
                      income.participantId === participantId &&
                      income.type === type,
                  )
                  .reduce((sum, income) => sum + (income.amount || 0), 0);

                return (
                  <div
                    key={type}
                    className={`flex justify-between items-center px-2 py-1 rounded cursor-pointer ${
                      selectedType === type ? "bg-green-100" : ""
                    } hover:bg-gray-100`}
                    onClick={() => onSelect(type)}
                  >
                    <Subheading>{incomeTypes[type].title}</Subheading>
                    <Subheading className="text-green-600">
                      {incomeSum > 0 ? `$${incomeSum.toLocaleString()}` : ""}
                    </Subheading>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default IncomeListSidebar;
